import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Snowflake, 
    Wind, 
    Waves,
    ChevronRight,
    Heart,
    Hand,
    Feather,
    HelpCircle,
    Home,
    Square,
    Flower2
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { ProgressDots } from '../components/brand/BrandProgress';
import { BoxBreathingVisual } from '../components/brand/BoxBreathingVisual';
import { VisualBLS, BLSIndicator } from '../components/brand/VisualBLS';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { ContextualActionZone } from '../components/brand/AppShell';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { CountdownRing } from '../components/brand/CountdownRing';
import { buildSteps, TOOL_TYPES, TOOL_TYPE_META } from '../components/tools/toolRegistry';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for paths
const PATH_ICONS = {
    Snowflake: Snowflake,
    Wind: Wind,
    Waves: Waves,
};

// Standalone tools that can be recommended
const STANDALONE_TOOLS = [
    {
        id: 'box-breathing',
        name: 'Box Breathing',
        icon: Square,
        description: 'A simple 4-4-4-4 breathing pattern',
        path: '/regulate/box-breathing',
        variant: 'sage',
        forStates: ['scattered', 'flooded'] // Recommended for these states
    },
    {
        id: 'calm-cycle',
        name: 'Calm Cycle',
        icon: Flower2,
        description: 'A slow, steady rhythm to settle activation',
        path: '/regulate/breathing-orb',
        variant: 'lavender',
        forStates: ['scattered'] // For activation/spin-up - needs rhythm to follow
    },
    {
        id: 'still-sphere',
        name: 'Still Sphere',
        icon: Flower2,
        description: 'A warm, quiet presence when everything feels like too much',
        path: '/regulate/still-sphere',
        variant: 'gold',
        forStates: ['frozen', 'flooded'] // For overwhelm/shutdown - needs stillness not rhythm
    }
];

const RegulationNavigator = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('select'); // select, grounding, reflect, complete
    const [selectedType, setSelectedType] = useState(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [ringKey, setRingKey] = useState(0);
    const [feedbackRating, setFeedbackRating] = useState(null);
    
    // Database-driven paths
    const [overwhelmTypes, setOverwhelmTypes] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Paths that should NOT appear in the Regulation Navigator selection
    // These have their own dedicated pages/tools
    const EXCLUDED_PATHS = ['co_regulation', 'micro_reset'];

    // Fetch paths from database
    useEffect(() => {
        const fetchPaths = async () => {
            try {
                const response = await fetch(`${API_URL}/api/tools/paths/all`);
                if (!response.ok) throw new Error('Failed to fetch paths');
                
                const paths = await response.json();
                
                // Transform paths into the format we need
                const transformedPaths = {};
                for (const path of paths) {
                    // Skip excluded paths - they have their own dedicated pages
                    if (EXCLUDED_PATHS.includes(path.path_id)) {
                        continue;
                    }
                    
                    // Fetch expanded path with tool data
                    const expandedRes = await fetch(`${API_URL}/api/tools/paths/${path.path_id}`);
                    if (expandedRes.ok) {
                        const expandedPath = await expandedRes.json();
                        
                        // Build steps from expanded tool data
                        const steps = expandedPath.expanded_steps?.map(step => ({
                            toolId: step.tool_id,
                            text: step.tool?.instruction || '',
                            duration: step.tool?.duration || 0,
                            isBreath: step.tool?.type === 'breath',
                            isGrounding: step.tool?.type === 'grounding',
                            isBLS: step.tool?.type === 'bls',
                            isVisualBLS: step.tool?.type === 'visual_bls',
                            isAnchoring: step.tool?.type === 'anchoring',
                            isReflection: step.tool?.type === 'reflection',
                            isClosing: step.tool?.type === 'closing',
                            isSomatic: step.tool?.type === 'somatic',
                            optional: step.tool?.optional || false,
                            cycles: step.tool?.cycles,
                            speed: step.tool?.speed,
                        })) || [];
                        
                        transformedPaths[path.path_id] = {
                            id: path.path_id,
                            name: path.name,
                            icon: PATH_ICONS[path.icon] || Snowflake,
                            description: path.description,
                            validation: path.validation_message,
                            variant: path.variant,
                            steps: steps
                        };
                    }
                }
                
                setOverwhelmTypes(transformedPaths);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching paths:', err);
                setError(err.message);
                // Fallback to static data if database fails
                setOverwhelmTypes(getStaticPaths());
                setLoading(false);
            }
        };
        
        fetchPaths();
    }, []);

    // Static fallback paths (in case database is unavailable)
    const getStaticPaths = () => ({
        frozen: {
            id: 'frozen',
            name: 'Frozen',
            icon: Snowflake,
            description: 'Stuck, distant, or slowed down',
            validation: 'Freezing is your body\'s way of protecting you. It makes sense.',
            variant: 'blue',
            steps: buildSteps([
                'shoulder_drop_breath',
                'finger_wiggle',
                'feet_grounding',
                'emotion_notice',
                'warmth_breath',
                'closing_frozen'
            ])
        },
        scattered: {
            id: 'scattered',
            name: 'Scattered',
            icon: Wind,
            description: 'Racing thoughts, hard to focus',
            validation: 'A busy mind is trying to solve everything at once. Let\'s slow down.',
            variant: 'gold',
            steps: buildSteps([
                'belly_breath',
                'hand_awareness',
                'knee_tapping',
                'color_notice',
                'long_exhale',
                'closing_scattered'
            ])
        },
        flooded: {
            id: 'flooded',
            name: 'Flooded',
            icon: Waves,
            description: 'Big emotions, hard to think',
            validation: 'Big feelings aren\'t a failure. They\'re information.',
            variant: 'lavender',
            steps: buildSteps([
                'chest_breath',
                'butterfly_hug',
                'safe_place',
                'visual_bls',
                'closing_flooded'
            ])
        }
    });

    // Feedback options for the simplified assessment
    const feedbackOptions = [
        { id: 'much-better', label: 'Much better', emoji: '😌', color: '#1D6B6B' },
        { id: 'a-little-better', label: 'A little better', emoji: '🙂', color: '#7D8E95' },
        { id: 'about-the-same', label: 'About the same', emoji: '😐', color: '#7D8E95' },
        { id: 'still-struggling', label: 'Still struggling', emoji: '😔', color: '#FFBB98' }
    ];

    // Helper functions
    const isBreathStep = (step) => step?.isBreath;
    const isReflectionStep = (step) => step?.isReflection;
    const isBLSStep = (step) => step?.isBLS;
    const isVisualBLSStep = (step) => step?.isVisualBLS;
    const isGroundingStep = (step) => step?.isGrounding;
    const isClosingStep = (step) => step?.isClosing;
    const isOptionalStep = (step) => step?.optional;
    const isAnchoringStep = (step) => step?.isAnchoring;
    const isSomaticStep = (step) => step?.isSomatic;
    
    // Get step duration from tool registry (already set on each step)
    const getStepDuration = (step) => {
        return step?.duration || 0;
    };
    
    // Check if step should show countdown ring
    const shouldShowRing = (step) => {
        const duration = getStepDuration(step);
        return duration > 0 && !step?.isBreath && !step?.isVisualBLS;
    };

    const handleSelectType = (type) => {
        setSelectedType(type);
        setStep('grounding');
        setCurrentStepIndex(0);
        setRingKey(prev => prev + 1);
    };

    const handleNext = () => {
        if (currentStepIndex < selectedType.steps.length - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
            setRingKey(prev => prev + 1); // Reset ring for new step
        } else {
            setStep('reflect');
        }
    };

    const handleSkip = () => {
        handleNext();
    };

    const handleExit = () => {
        navigate('/regulate');
    };

    const handleExitHome = () => {
        navigate('/home');
    };

    const handleStartOver = () => {
        setStep('select');
        setSelectedType(null);
        setCurrentStepIndex(0);
        setFeedbackRating(null);
    };

    // Submit feedback to analytics
    const submitFeedback = async (rating) => {
        setFeedbackRating(rating);
        try {
            await fetch(`${API_URL}/api/tools/feedback?path_id=${selectedType.id}&rating=${rating}`, {
                method: 'POST'
            });
        } catch (err) {
            console.error('Failed to submit feedback:', err);
        }
        setStep('complete');
    };

    // Get step indicator label (gentler than "Step X of Y")
    const getStepLabel = () => {
        const current = currentStepIndex + 1;
        const total = selectedType.steps.length;
        if (current === 1) return 'Starting gentle';
        if (current === total) return 'Almost there';
        return `${current} of ${total}`;
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 rounded-full border-2 border-[#7D8E95] border-t-transparent animate-spin mx-auto mb-4" />
                    <SmallText className="text-[var(--orenda-text-muted)]">Loading tools...</SmallText>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent">
            {/* Orenda Module Header */}
            <ModuleHeader
                title="Regulation Navigator"
                onBack={() => {
                    if (step === 'select') {
                        navigate('/home?tab=regulation');
                    } else {
                        setStep('select');
                        setSelectedType(null);
                        setCurrentStepIndex(0);
                    }
                }}
                backDestination="/home?tab=regulation"
                exitDestination="/home?tab=regulation"
            />

            <main className="max-w-xl mx-auto px-6 py-8">

                {/* SELECT: Choose your overwhelm type */}
                {step === 'select' && (
                    <div className="animate-fade-in">
                        {/* Welcoming header */}
                        <div className="text-center mb-8">
                            <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            
                            <Heading as="h2" size="text-xl" className="mb-3">
                                Let's find what helps
                            </Heading>
                            
                            <BodyText className="text-[var(--orenda-text-secondary)] leading-relaxed">
                                Which of these feels closest to where you are?
                            </BodyText>
                            <SmallText className="text-[var(--orenda-text-muted)] block mt-2">
                                There's no wrong answer.
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {Object.values(overwhelmTypes).map((type, index) => {
                                const glassVariants = {
                                    blue: 'glass-card-sage',
                                    sage: 'glass-card-sage',
                                    lavender: 'glass-card-lavender',
                                    rose: 'glass-card-rose',
                                    gold: 'glass-card-gold'
                                };
                                
                                // Hardcode different icon colors for each type
                                const iconStyles = {
                                    frozen: { background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)' }, // Teal
                                    scattered: { background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)' }, // Coral
                                    flooded: { background: 'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)' }, // Dusty Rose
                                };
                                
                                return (
                                    <button
                                        key={type.id}
                                        className={`w-full text-left rounded-2xl p-5 ${glassVariants[type.variant] || 'glass-card'} card-hover transition-all duration-200 animate-fade-in`}
                                        style={{ animationDelay: `${index * 80}ms` }}
                                        onClick={() => handleSelectType(type)}
                                        data-testid={`overwhelm-${type.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div 
                                                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                                                style={{ ...iconStyles[type.id], color: 'white' }}
                                            >
                                                <type.icon className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1">
                                                <Heading as="h3" size="text-base" className="mb-0.5">
                                                    {type.name}
                                                </Heading>
                                                <SmallText className="text-[var(--orenda-text-muted)]">
                                                    {type.description}
                                                </SmallText>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-[var(--orenda-border)]" />
                                        </div>
                                    </button>
                                );
                            })}

                            {/* "Not sure" option */}
                            <button
                                className="w-full text-left rounded-2xl p-5 glass-card border-dashed card-hover transition-all duration-200 animate-fade-in"
                                style={{ animationDelay: '240ms' }}
                                onClick={() => handleSelectType(overwhelmTypes.flooded)}
                                data-testid="overwhelm-unsure"
                            >
                                <div className="flex items-center gap-4">
                                    <IconContainer variant="neutral" size="md" useGradient={true}>
                                        <HelpCircle className="w-5 h-5" />
                                    </IconContainer>
                                    <div className="flex-1">
                                        <Heading as="h3" size="text-base" className="mb-0.5">
                                            I'm not sure
                                        </Heading>
                                        <SmallText className="text-[var(--orenda-text-muted)]">
                                            That's okay. We'll start gentle.
                                        </SmallText>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-[var(--orenda-border)]" />
                                </div>
                            </button>
                        </div>

                        {/* Quick standalone tools */}
                        <div className="mt-8 mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] text-center block mb-4">
                                Or try a quick tool
                            </SmallText>
                            <div className="flex gap-3 justify-center">
                                {STANDALONE_TOOLS.map((tool) => (
                                    <button
                                        key={tool.id}
                                        onClick={() => navigate(tool.path)}
                                        className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white border border-[var(--orenda-border)] hover:border-[var(--orenda-sage)]/50 transition-all hover:scale-[1.02]"
                                        data-testid={`quick-tool-${tool.id}`}
                                    >
                                        <IconContainer variant={tool.variant} size="md" useGradient={true}>
                                            <tool.icon className="w-5 h-5" />
                                        </IconContainer>
                                        <SmallText className="text-[var(--orenda-text-secondary)] font-medium">
                                            {tool.name}
                                        </SmallText>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Safety footer */}
                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="You can pause anytime."
                        />
                    </div>
                )}

                {/* GROUNDING: Simplified, gentler sequence */}
                {step === 'grounding' && selectedType && (
                    <div className="flex flex-col min-h-[70vh]">
                        {/* Validation - only on first step */}
                        {currentStepIndex === 0 && (
                            <div className="mb-6 p-4 rounded-2xl bg-[var(--orenda-soft-teal)]/10 border border-[var(--orenda-soft-teal)]/20 animate-fade-in">
                                <BodyText className="text-[var(--orenda-text-secondary)] text-center italic text-sm leading-relaxed">
                                    {selectedType.validation}
                                </BodyText>
                            </div>
                        )}

                        {/* Progress - gentler language */}
                        <div className="text-center mb-4">
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                {getStepLabel()}
                            </SmallText>
                        </div>

                        {/* [Main Tool Container — contains instruction, tool visual, and countdown ring] */}
                        <div className="flex-1 flex items-center justify-center py-4">
                            <div className="glass-card rounded-2xl p-5"
                                variant="elevated"
                                padding="xl"
                                className="w-full rounded-3xl animate-fade-in"
                                key={currentStepIndex}
                            >
                                <div className="text-center py-4">
                                    {/* Step icon */}
                                    {isClosingStep(selectedType.steps[currentStepIndex]) && (
                                        <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-4">
                                            <Heart className="w-5 h-5" />
                                        </IconContainer>
                                    )}
                                    {(isGroundingStep(selectedType.steps[currentStepIndex]) || isBLSStep(selectedType.steps[currentStepIndex]) || isVisualBLSStep(selectedType.steps[currentStepIndex])) && !isBreathStep(selectedType.steps[currentStepIndex]) && (
                                        <IconContainer variant={selectedType.variant} size="md" useGradient={true} className="mx-auto mb-4">
                                            <selectedType.icon className="w-4 h-4" />
                                        </IconContainer>
                                    )}
                                    
                                    {/* Instruction text - inside container, above tool */}
                                    <BodyText className="text-lg text-[var(--orenda-text-primary)] leading-relaxed mb-6 max-w-md mx-auto">
                                        {selectedType.steps[currentStepIndex].text}
                                    </BodyText>
                                    {isOptionalStep(selectedType.steps[currentStepIndex]) && (
                                        <SmallText className="text-[var(--orenda-text-muted)] mb-4 block">
                                            Skip if this doesn't feel right.
                                        </SmallText>
                                    )}
                                    
                                    {/* Tool visual with integrated countdown */}
                                    {isBreathStep(selectedType.steps[currentStepIndex]) && (
                                        <div>
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--orenda-mist-blue)]/30 mb-6">
                                                <Square className="w-3 h-3 text-[#7D8E95]" />
                                                <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                                    Box Breathing (4-4-4-4)
                                                </SmallText>
                                            </div>
                                            <BoxBreathingVisual 
                                                size="md"
                                                variant={selectedType.variant}
                                                autoStart={true}
                                                showControls={false}
                                                showPhaseIndicators={true}
                                            />
                                        </div>
                                    )}

                                    {isBLSStep(selectedType.steps[currentStepIndex]) && (
                                        <div>
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7D8E95]/20 mb-6">
                                                <Hand className="w-3 h-3 text-[#7D8E95]" />
                                                <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                                    Gentle tapping
                                                </SmallText>
                                            </div>
                                            <div className="flex justify-center mb-4">
                                                <BLSIndicator variant={selectedType.variant} speed={1200} />
                                            </div>
                                            {/* Integrated countdown ring */}
                                            <div className="flex justify-center">
                                                <CountdownRing
                                                    key={ringKey}
                                                    duration={getStepDuration(selectedType.steps[currentStepIndex])}
                                                    size="md"
                                                    variant="lavender"
                                                    autoStart={true}
                                                    showOnComplete={true}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {isVisualBLSStep(selectedType.steps[currentStepIndex]) && (
                                        <div>
                                            <div 
                                                className="w-full mb-4"
                                                style={{
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    contain: 'layout',
                                                }}
                                            >
                                                <div className="overflow-visible">
                                                    <VisualBLS 
                                                        size="lg"
                                                        variant={selectedType.variant}
                                                        cycles={selectedType.steps[currentStepIndex].cycles || 3}
                                                        duration={selectedType.steps[currentStepIndex].speed || 3}
                                                        showInstructions={false}
                                                    />
                                                </div>
                                            </div>
                                            {/* Integrated countdown ring */}
                                            <div className="flex justify-center">
                                                <CountdownRing
                                                    key={ringKey}
                                                    duration={getStepDuration(selectedType.steps[currentStepIndex])}
                                                    size="md"
                                                    variant={selectedType.variant === 'blue' ? 'greyBlue' : selectedType.variant}
                                                    autoStart={true}
                                                    showOnComplete={true}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {isReflectionStep(selectedType.steps[currentStepIndex]) && (
                                        <div>
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--orenda-warm-sand)]/50">
                                                <Feather className="w-3 h-3 text-[var(--orenda-slate)]" />
                                                <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                                    Optional reflection
                                                </SmallText>
                                            </div>
                                        </div>
                                    )}

                                    {isAnchoringStep(selectedType.steps[currentStepIndex]) && (
                                        <div>
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7D8E95]/20">
                                                <Heart className="w-3 h-3 text-[#7D8E95]" />
                                                <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                                    Anchoring
                                                </SmallText>
                                            </div>
                                        </div>
                                    )}

                                    {/* Somatic/body awareness steps with countdown ring */}
                                    {isSomaticStep(selectedType.steps[currentStepIndex]) && (
                                        <div className="flex justify-center">
                                            <CountdownRing
                                                key={ringKey}
                                                duration={getStepDuration(selectedType.steps[currentStepIndex])}
                                                size="lg"
                                                variant={selectedType.variant === 'blue' ? 'greyBlue' : selectedType.variant}
                                                autoStart={true}
                                                showOnComplete={true}
                                            />
                                        </div>
                                    )}

                                    {/* Grounding steps with integrated countdown ring */}
                                    {isGroundingStep(selectedType.steps[currentStepIndex]) && (
                                        <div className="flex justify-center">
                                            <CountdownRing
                                                key={ringKey}
                                                duration={getStepDuration(selectedType.steps[currentStepIndex])}
                                                size="lg"
                                                variant={selectedType.variant === 'blue' ? 'greyBlue' : selectedType.variant}
                                                autoStart={true}
                                                showOnComplete={true}
                                            />
                                        </div>
                                    )}

                                    {/* Closing step - no timer, just text */}
                                    {isClosingStep(selectedType.steps[currentStepIndex]) && (
                                        <div>
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7D8E95]/20">
                                                <Heart className="w-3 h-3 text-[#1D6B6B]" />
                                                <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                                    Closing
                                                </SmallText>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Progress Dots */}
                        <div className="flex justify-center mb-6">
                            <ProgressDots 
                                total={selectedType.steps.length} 
                                current={currentStepIndex} 
                            />
                        </div>

                        {/* Gentle exit always visible */}
                        <div className="text-center">
                            <TertiaryButton
                                onClick={handleExit}
                                className="text-[var(--orenda-text-muted)] text-sm"
                            >
                                Come back later
                            </TertiaryButton>
                        </div>
                        
                        {/* Bottom spacer - breathwork for animation clearance */}
                        <BottomBarSpacer height="breathwork" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Grounding step */}
                {step === 'grounding' && selectedType && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => {
                                if (currentStepIndex === selectedType.steps.length - 1) {
                                    setStep('reflect');
                                } else {
                                    handleNext();
                                }
                            }}
                            className="w-full"
                            size="action"
                            data-testid="next-btn"
                        >
                            {currentStepIndex === selectedType.steps.length - 1 ? 'I\'m done' : 'Next'}
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        
                        {isOptionalStep(selectedType.steps[currentStepIndex]) && (
                            <TertiaryButton
                                onClick={handleSkip}
                                className="w-full text-[var(--orenda-text-muted)] text-sm"
                            >
                                Skip this one
                            </TertiaryButton>
                        )}
                    </PersistentBottomBar>
                )}

                {/* REFLECT: Optional emotion check-in after grounding */}
                {step === 'reflect' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-xl" className="mb-2">
                                How are you feeling now?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                Your feedback helps us improve
                            </BodyText>
                        </div>

                        <div className="space-y-3 max-w-sm mx-auto">
                            {feedbackOptions.map(option => (
                                <button
                                    key={option.id}
                                    onClick={() => submitFeedback(option.id)}
                                    className={`w-full p-4 rounded-2xl border-2 transition-all duration-200 flex items-center gap-4 ${
                                        feedbackRating === option.id 
                                            ? 'border-[#1D6B6B] bg-[#7D8E95]/10' 
                                            : 'border-white/40 bg-white/50 hover:border-[#7D8E95]/50'
                                    }`}
                                    data-testid={`feedback-${option.id}`}
                                >
                                    <span className="text-2xl">{option.emoji}</span>
                                    <span className="text-[#1D6B6B] font-medium">{option.label}</span>
                                </button>
                            ))}
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {/* COMPLETE: Contextual response based on feedback */}
                {step === 'complete' && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in">
                        <IconContainer variant="sage" size="xl" useGradient={true} className="mb-6">
                            <Heart className="w-8 h-8" />
                        </IconContainer>
                        
                        {(feedbackRating === 'much-better' || feedbackRating === 'a-little-better') && (
                            <>
                                <Heading as="h2" size="text-xl" className="mb-3">
                                    That's wonderful
                                </Heading>
                                <BodyText className="text-[var(--orenda-text-secondary)] max-w-sm mb-2 leading-relaxed">
                                    Your nervous system responded well to this practice.
                                </BodyText>
                                <SmallText className="text-[var(--orenda-text-muted)] mb-6">
                                    Remember, you can return here anytime you need.
                                </SmallText>
                            </>
                        )}
                        
                        {feedbackRating === 'about-the-same' && (
                            <>
                                <Heading as="h2" size="text-xl" className="mb-3">
                                    That's okay
                                </Heading>
                                <BodyText className="text-[var(--orenda-text-secondary)] max-w-sm mb-2 leading-relaxed">
                                    Sometimes it takes a few tries for our body to settle.
                                </BodyText>
                                <SmallText className="text-[var(--orenda-text-muted)] mb-6">
                                    Would you like to try another approach?
                                </SmallText>
                            </>
                        )}
                        
                        {feedbackRating === 'still-struggling' && (
                            <>
                                <Heading as="h2" size="text-xl" className="mb-3">
                                    We hear you
                                </Heading>
                                <BodyText className="text-[var(--orenda-text-secondary)] max-w-sm mb-2 leading-relaxed">
                                    Some moments are harder than others. That's not a failure — it's being human.
                                </BodyText>
                                <SmallText className="text-[var(--orenda-text-muted)] mb-6">
                                    Let's try a different tool that might help more.
                                </SmallText>
                            </>
                        )}

                        {/* Soft closing message */}
                        <div className="mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Your nervous system is doing its best.
                            </SmallText>
                        </div>
                        
                        {/* Calm Cycle recommendation for scattered and flooded paths */}
                        {(selectedType === 'scattered' || selectedType === 'flooded') && (
                            <div 
                                onClick={() => navigate('/regulate/breathing-orb')}
                                className="mb-6 p-4 bg-gradient-to-r from-[#F5EED9] to-[#FFBB98]/30 rounded-2xl border border-white/40/30 cursor-pointer hover:shadow-md transition-all"
                                data-testid="calm-cycle-recommendation"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-[#FFBB98]/20 flex items-center justify-center">
                                        <div className="w-4 h-4 rounded-full bg-[#FFBB98]/60"></div>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <SmallText className="font-medium text-[#8A7A5A]">
                                            Settle with Calm Cycle
                                        </SmallText>
                                        <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                            A steady rhythm to help your system downshift • ~3 min
                                        </SmallText>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-[#8A7A5A]" />
                                </div>
                            </div>
                        )}
                        
                        {/* Bottom spacer - extended for two-button bar */}
                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Complete step */}
                {step === 'complete' && (
                    <PersistentBottomBar>
                        {(feedbackRating === 'much-better' || feedbackRating === 'a-little-better') ? (
                            <>
                                <PrimaryButton 
                                    onClick={handleExit}
                                    className="w-full"
                                    size="action"
                                    data-testid="return-to-regulate-btn"
                                >
                                    <Home className="w-4 h-4 mr-1.5" />
                                    Back to Regulation Tools
                                </PrimaryButton>
                                
                                <TertiaryButton
                                    onClick={handleExitHome}
                                    className="w-full text-[var(--orenda-text-muted)] text-sm"
                                    data-testid="return-home-btn"
                                >
                                    Return Home
                                </TertiaryButton>
                            </>
                        ) : (
                            <>
                                <PrimaryButton 
                                    onClick={handleStartOver}
                                    className="w-full"
                                    size="action"
                                    data-testid="try-another-btn"
                                >
                                    Try a different approach
                                </PrimaryButton>
                                
                                <TertiaryButton
                                    onClick={handleExit}
                                    className="w-full text-[var(--orenda-text-muted)] text-sm"
                                    data-testid="done-btn"
                                >
                                    I'm done for now
                                </TertiaryButton>
                            </>
                        )}
                    </PersistentBottomBar>
                )}
            </main>
        </div>
    );
};

export default RegulationNavigator;
